import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga4';
import { isBankingRoutePath } from '@/auth/querySessionTokens';

// ----------------------------------------------------------------------
// Constantes
// ----------------------------------------------------------------------

const BRAND = 'Cakto';
const PAGEVIEW_DEBOUNCE_MS = 350;

// ----------------------------------------------------------------------
// Helpers: título do documento
// ----------------------------------------------------------------------

function stripEmojis(str) {
  if (typeof str !== 'string') return '';
  return str
    // eslint-disable-next-line no-misleading-character-class -- emoji/symbol removal for GA4
    .replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Título no formato "Cakto | Área | Tela". Sem emojis, sem espaços extras.
 * @param {string} [area] - Ex: "Dashboard", "Auth"
 * @param {string} [screen] - Ex: "Página Inicial", "Entrar"
 */
export function getPageTitle(area, screen) {
  const a = stripEmojis(String(area || '').trim());
  const s = stripEmojis(String(screen || '').trim());

  if (!a && !s) return BRAND;
  if (!s) return a ? `${BRAND} | ${a}` : BRAND;
  if (!a) return `${BRAND} | ${s}`;
  return `${BRAND} | ${a} | ${s}`;
}

// ----------------------------------------------------------------------
// GA4: pageview (GA é inicializado no App.js; aqui só dispara o evento)
// ----------------------------------------------------------------------

let pageViewTimeoutId = null;
let lastSentPath = '';
let hasSentInSession = false;

export function getTelemetryPagePath() {
  if (typeof window === 'undefined') return '';
  const path = (window.location.pathname || '/').replace(/\/$/, '') || '/';

  return isBankingRoutePath(path) ? path : path + (window.location.search || '');
}

function sendPageView(page, title) {
  if (page === lastSentPath) return;
  lastSentPath = page;
  hasSentInSession = true;
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'cakto_page_view',
      page_path: page,
      page_title: title,
    });
  } else {
    ReactGA.send({ hitType: 'pageview', page, title });
  }
}

// ----------------------------------------------------------------------
// Componente
// ----------------------------------------------------------------------

/**
 * Define o título do documento e envia um pageview ao GA4.
 *
 * @param {string} area - Ex: "Dashboard", "Auth"
 * @param {string} screen - Ex: "Página Inicial"
 */
export function PageTitle({ area = '', screen = '' }) {
  const title = getPageTitle(area, screen);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const page = getTelemetryPagePath();
    const isFirstSendOfSession = !hasSentInSession;
    const delay = isFirstSendOfSession ? 0 : PAGEVIEW_DEBOUNCE_MS;

    if (pageViewTimeoutId) clearTimeout(pageViewTimeoutId);
    pageViewTimeoutId = setTimeout(() => {
      pageViewTimeoutId = null;
      sendPageView(page, title);
    }, delay);

    return () => {
      if (pageViewTimeoutId) clearTimeout(pageViewTimeoutId);
    };
  }, [title]);

  return (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );
}

PageTitle.propTypes = {
  area: PropTypes.string,
  screen: PropTypes.string,
};
